import { useNavigate } from "react-router-dom";
import { Scale, CheckCircle, AlertTriangle, Clock, FileText } from "lucide-react";

interface LandasanHukumItem {
  id: string;
  judul: string;
  nomor_dokumen?: string;
  bentuk_dokumen_kode: string;
  bentuk_dokumen_nama: string;
  status_kode: string;
  status_nama: string;
  tanggal_berlaku?: string;
  tanggal_berakhir?: string;
  is_active: boolean;
}

interface LandasanHukumPanelProps {
  items: LandasanHukumItem[];
  searchQuery?: string;
  onViewAll?: () => void;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function LandasanHukumPanel({ items, searchQuery = "", onViewAll }: LandasanHukumPanelProps) {
  const navigate = useNavigate();

  const filtered = searchQuery
    ? items.filter(
        (item) =>
          item.judul.toLowerCase().includes(searchQuery) ||
          item.nomor_dokumen?.toLowerCase().includes(searchQuery),
      )
    : items;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border-b border-blue-100">
        <Scale className="h-4 w-4 text-blue-600" />
        <h3 className="text-sm font-bold text-blue-800">LANDASAN HUKUM</h3>
        <span className="text-xs text-blue-500 ml-1">(D01 - Regulasi)</span>
        <span className="ml-auto flex items-center gap-2">
          {onViewAll && (
            <button
              onClick={onViewAll}
              className="text-[10px] text-blue-600 hover:text-blue-800 underline font-medium"
            >
              Lihat Semua
            </button>
          )}
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
            {filtered.length}
          </span>
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400">
            Tidak ada dokumen regulasi
          </div>
        ) : (
          filtered.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(`/documents/${item.id}`)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 flex-shrink-0">
                  {item.is_active ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400">{item.bentuk_dokumen_nama}</span>
                    <span
                      className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        item.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {item.is_active ? "Berlaku" : "Kadaluarsa"}
                    </span>
                  </div>
                  {item.nomor_dokumen && (
                    <p className="text-xs font-mono text-blue-600 mt-0.5">{item.nomor_dokumen}</p>
                  )}
                  <p className="text-sm font-medium text-gray-900 mt-0.5 line-clamp-2 group-hover:text-blue-700">
                    {item.judul}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    {item.tanggal_berlaku && (
                      <span>Berlaku: {formatDate(item.tanggal_berlaku)}</span>
                    )}
                    {item.tanggal_berakhir && (
                      <span>Berakhir: {formatDate(item.tanggal_berakhir)}</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
